import { Button, Spinner, Typography } from "@material-tailwind/react";
import React, { Fragment, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { ReactComponent as Copy } from "../../Assets/Svg/Copy.svg";
import { ReactComponent as Pencil } from "../../Assets/Svg/EditPencil.svg";
import { ReactComponent as Trash } from "../../Assets/Svg/Trash.svg";
import CitationComponent from "../../Components/Functionality/CitationComponent";
import NoData from "../../Components/Functionality/NoData";
import SortByFilter from "../../Components/Functionality/SortByFilter";
import CitationPopup from "../../Components/Popup/CitationPopup";
import InfoPopup from "../../Components/Popup/InfoPopup";
import { AlertWithContent } from "../../Components/UI/Alert/Alert";
import ClickableChip from "../../Components/UI/Chip/ClickableChip";
import SearchField from "../../Components/UI/Inputs/SearchField";
import { usePlan } from "../../Context/PlanContext";
import {
  useSubworkspaceStore,
  useUserStore,
  useWorkspaceStore,
} from "../../Context/store";
import { handleUpgradePlan } from "../../Services/SweetAlert";
import useDebounce from "../../Services/useDebounce";
import {
  LIBRARY_STORAGE,
  LibrarySortByOptions,
  primaryColor,
  warningFullStorageUsed,
  warningHalfStorageUsed,
} from "../../Utils/Constant";
import { copyToClipboard } from "../../Utils/Helper";
import { useGetCategoriesByWorkspaceId } from "../../Web-Hooks/Categories/use-categories";
import {
  useCitationRemove,
  useDeleteBlob,
  useGetCitationUserWise,
  useUpdateCitationUserWise,
  useUploadCitationUserWise,
} from "../../Web-Hooks/LibraryAPI/use-citation";
import LibraryCard from "./LibraryCard";
import StorageUsage from "./StorageUsed";

const Library = () => {
  const location = useLocation();
  const { search: searchParams } = location;
  const navigate = useNavigate();
  const query = new URLSearchParams(searchParams);
  const { currentPlan } = usePlan();
  const sortByDefault = query.get("sortBy");
  const isFromDebate = query.get("isFromDebate");
  const isFromAddArgument = query.get("isFromAddArgument");
  const isFromDebatePhoto = query.get("isFromDebatePhoto");
  const [search, setSearch] = useState("");
  const deferredSearch = useDebounce(search, 500);
  const [isEdit, setIsEdit] = useState(false);
  const [selected, setSelected] = useState("Arts");
  const [isFromExternal, setIsFromExternal] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState({
    citationUrl: "",
    citationNote: "",
    citationType: "",
    citationFile: null,
  });
  const [deletePopup, setDeletePopup] = useState(null);
  const [option, setOption] = useState(
    () =>
      LibrarySortByOptions.find((option) => option.value === sortByDefault) || {
        label: "Most Recent",
        value: "recent",
      }
  );
  const [citationPopup, setCitationPopup] = useState(false);
  const { user } = useUserStore();
  const { selectedSubWorkspace } = useSubworkspaceStore();
  const { selectedWorkspace } = useWorkspaceStore();
  const { data: categoryList, isLoading } = useGetCategoriesByWorkspaceId(
    selectedWorkspace?.workSpaceId,
    user?.userId
  );

  const {
    mutateAsync: uploadCitationOnLibrary,
    isPending: isUploadCitationPending,
  } = useUploadCitationUserWise();
  const { mutateAsync: updateCitation, isPending: isUpdatingCitationPending } =
    useUpdateCitationUserWise();

  const { data, isLoading: isLoadingCitation } = useGetCitationUserWise(
    user?.userId,
    selected?.categoryName,
    deferredSearch,
    option?.value
  );

  const citationList = data?.citations;
  const userStorage = data?.userStorage;

  const totalStorage = currentPlan?.libraryStorage;
  const isStorageMoreThanHalf = userStorage >= totalStorage / 2;

  const isStorageFull = userStorage >= totalStorage;

  const { mutateAsync: removeCitation, isPending: isCitationDeletePending } =
    useCitationRemove();
  const { mutate: deleteBlob } = useDeleteBlob();

  const Actions = [
    {
      SVG: Copy,
      title: "Copy",
      handleClick: (citation) => {
        if (!isFromDebate && !isFromDebatePhoto && !isFromAddArgument) {
          copyToClipboard(citation?.refUrl);
          return;
        }
        setSelectedCitation({
          citationUrl: citation?.refUrl,
          citationNote: citation?.note,
          citationType: citation?.refFileType,
          citationSize: citation?.size,
        });
        setCitationPopup(true);
      },
    },
    {
      SVG: Pencil,
      title: "Edit",
      isDisable: isFromDebate || isFromAddArgument,
      handleClick: (citation) => {
        setCitationPopup(true);
        setIsFromExternal(citation?.refFileType === "url");
        setIsEdit(true);
        setSelectedCitation({
          citationUrl: citation?.refUrl,
          citationNote: citation?.note,
          citationType: citation?.refFileType,
          citationId: citation?.id,
          citationTitle: citation?.title,
        });
      },
    },
    {
      SVG: Trash,
      title: "Delete",
      isDisable: isFromDebate || isFromAddArgument,
      handleClick: (citation) => {
        Swal.fire({
          title: "Are you sure you want to delete this citation?",
          text: "You won't be able to revert this!",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: primaryColor,
          confirmButtonText: "Yes, delete it!",
          showLoaderOnConfirm: true,
          allowOutsideClick: () => !Swal.isLoading(),
        }).then(async (result) => {
          if (result.isConfirmed) {
            await handleDelete(citation);
          }
        });
      },
    },
  ];

  const handleDelete = async (citation) => {
    if (citation?.refFileType !== "url") {
      deleteBlob({ fileUrl: citation?.refUrl });
    }
    removeCitation({ id: citation?.id });
    setSelected((prev) => ({
      ...prev,
      totalData: prev?.totalData ? prev?.totalData - 1 : prev?.totalData,
    }));
    setDeletePopup(null);
  };

  const handleSelect = (title) => {
    if (title?.categoryName === selected?.categoryName) {
      return;
    } else {
      setSelected(title);
    }
  };

  const handleCitationSubmit = async (selectedCitation) => {
    const { userId } = user || {};
    const { categoryName } = selected || {};
    const {
      citationNote,
      citationType,
      citationFile,
      citationUrl,
      citationTitle,
    } = selectedCitation;
    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("SubWorkSpaceId", selectedSubWorkspace?.subWorkSpaceId);
    formData.append("CreateAt", new Date().toISOString());
    formData.append("Category", categoryName || "");
    formData.append("Note", citationNote);
    formData.append("File", citationFile);
    formData.append("FileSize", citationFile?.size || 0);
    formData.append("FileType", citationType || "");
    if (citationType === "url") {
      formData.delete("File");
      formData.delete("FileSize");
      formData.append("RefUrl", citationUrl);
      formData.append("citationTitle", citationTitle);
    }

    if (formData) {
      const data = await uploadCitationOnLibrary(formData);
      if (data?.error) return; // ! If Something breaks then remove this line
      setSelected((prev) => ({ ...prev, totalData: prev?.totalData + 1 }));
      return data;
    }
  };

  const handleCitationUpdate = async () => {
    const { userId } = user || {};
    const { citationNote, citationId } = selectedCitation;
    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("SubWorkSpaceId", selectedSubWorkspace?.subWorkSpaceId);
    formData.append("updatedAt", new Date().toISOString());
    formData.append("Note", citationNote);
    formData.append("id", citationId);

    if (formData) {
      await updateCitation(formData)
        .then((res) => {
          setSelectedCitation((prev) => ({
            ...prev,
            citationUrl: res.data,
          }));
          setSelectedCitation({
            citationNote: "",
            citationType: "",
            citationUrl: "",
            citationFile: null,
            citationId: "",
          });
          setCitationPopup(false);
        })
        .catch(() => {
          toast.error("Error uploading citation");
          setSelectedCitation({
            citationNote: "",
            citationType: "",
            citationUrl: "",
          });
          setCitationPopup(false);
        });
    }
  };

  const renderLoaderChip = () => {
    const numChips = 25; // Number of divs to render

    return Array.from({ length: numChips }).map((_, index) => (
      <div
        key={index}
        className="py-2 px-3 w-24 h-12 border items-center border-debatePalette-buttonBorder opacity-20 justify-center gap-2 flex rounded-md animate__fadeIn"
      >
        <div className="w-[60%] h-5 bg-debatePalette-hr opacity-20"></div>
        <div className="w-[20%] h-5 rounded-full bg-debatePalette-hr opacity-20"></div>
      </div>
    ));
  };

  const renderLoaderCitations = () => {
    const numChips = 4; // Number of divs to render

    return Array.from({ length: numChips }).map((_, index) => (
      <div
        key={index}
        className="flex gap-2 h-14 w-full items-center animate__fadeIn"
      >
        <figure className="w-14 h-14 bg-debatePalette-hr opacity-20"></figure>
        <div className="h-full p-4 flex items-center rounded-md flex-1 border bg-debatePalette-hr opacity-20"></div>
      </div>
    ));
  };

  useEffect(() => {
    if (categoryList && categoryList.length > 0) {
      setSelected(categoryList[0]);
    }
  }, [setSelected, isLoading]);

  return (
    <Fragment>
      <section className="flex w-full flex-col px-3 md:px-8 py-2 gap-4 justify-start items-start overflow-y-auto">
        <div className="flex w-full">
          {isStorageMoreThanHalf && !isStorageFull && (
            <AlertWithContent
              color={"orange"}
              Message={
                <Fragment>
                  <Typography color="white" variant="lead">
                    You're Halfway There!
                  </Typography>
                  <Typography color="white" variant="paragraph">
                    {warningHalfStorageUsed}
                  </Typography>
                </Fragment>
              }
            />
          )}
          {isStorageFull && (
            <AlertWithContent
              color={"red"}
              Message={
                <Fragment>
                  <Typography color="white" variant="lead">
                    Your Storage is Running Out!
                  </Typography>
                  <Typography color="white" variant="paragraph">
                    {warningFullStorageUsed}
                  </Typography>
                </Fragment>
              }
            />
          )}
        </div>
        <StorageUsage used={userStorage} total={totalStorage} />

        <div className="flex items-center gap-4 w-full justify-between relative">
          <div className="w-full sm:w-3/4 lg:w-2/4">
            <SearchField
              id="library-search"
              name="library-search"
              search={search}
              setSearch={setSearch}
            />
          </div>
          {isStorageFull ? (
            <Button
              variant="filled"
              className=" text-debatePalette-title bg-primary"
              onClick={() =>
                handleUpgradePlan(LIBRARY_STORAGE, () => {
                  navigate("/upgrade-plan?plan=base-plan");
                })
              }
            >
              <span className="text-inherit" id="upload-citation-library">
                Upload Media
              </span>
            </Button>
          ) : (
            <CitationComponent
              MenuHandlerComponent={
                <Button
                  variant="filled"
                  className=" text-debatePalette-title bg-primary"
                >
                  <span className="text-inherit" id="upload-citation-library">
                    Upload Media
                  </span>
                </Button>
              }
              isLoadingFromHandleSubmit={isUploadCitationPending}
              handleCitationSubmit={async (citation) =>
                await handleCitationSubmit(citation)
              }
              isImageType={false}
            />
          )}
        </div>

        <div className="flex justify-start gap-2 items-center flex-wrap">
          {isLoading && renderLoaderChip()}
          {!isLoading &&
            categoryList?.map((category, i) => (
              <ClickableChip
                totalData={category?.totalData}
                key={category?.categoryId}
                selected={selected}
                title={category.categoryName}
                SVG={category.thumbnail}
                isSVG={false}
                handleClick={() => handleSelect(category)}
              />
            ))}
          {/* {AddCitationTag.map((category, i) => (
            <ClickableChip
              key={category?.categoryId}
              selected={selected}
              title={category.categoryName}
              SVG={category.thumbnail}
              isSVG={false}
              handleClick={() => setOpen(true)}
            />
          ))} */}
        </div>

        <div className="flex justify-between w-full h-16 border-b border-debatePalette-buttonBorder py-4">
          {selected ? (
            <ClickableChip
              totalData={selected?.totalData}
              selected={selected}
              title={selected?.categoryName}
              SVG={selected?.thumbnail}
              isSVG={false}
            />
          ) : (
            <div className="flex h-0"></div>
          )}
          <SortByFilter
            id={"library-sort-by"}
            Options={LibrarySortByOptions}
            setOption={setOption}
            defaultValue={option}
          />
        </div>
        <div
          className={`flex mt-4 flex-col gap-2 w-full ${
            citationList?.length > 0 ? "h-full" : "max-h-64"
          } overflow-y-auto`}
        >
          {isLoadingCitation && renderLoaderCitations()}
          {!isLoadingCitation && citationList?.length === 0 && (
            <NoData message={"No citations found"} size={"w-full"} />
          )}
          {
            <div className="flex flex-col gap-2">
              {citationList?.length > 0 &&
                citationList?.map((citation, i) => (
                  <LibraryCard
                    Actions={Actions}
                    key={citation.id}
                    Citation={citation}
                  />
                ))}
            </div>
          }
        </div>
      </section>
      {citationPopup && isUploadCitationPending ? (
        <Spinner />
      ) : (
        <CitationPopup
          setSelectedCitation={setSelectedCitation}
          popupOpen={citationPopup}
          selectedCitation={selectedCitation}
          setPopupOpen={(pop) => {
            setCitationPopup(pop);
            setIsFromExternal(pop);
          }}
          handleSubmit={
            isEdit
              ? handleCitationUpdate
              : () => handleCitationSubmit(selectedCitation)
          }
          isFromExternalCitation={isFromExternal}
          isUploadCitationPending={
            isUploadCitationPending || isUpdatingCitationPending
          }
          isEdit={isEdit}
        />
      )}

      {deletePopup && (
        <InfoPopup
          title={"Delete"}
          open={deletePopup ? true : false}
          description={"Are you sure you want to delete this citation"}
          btn1={"cancel"}
          btn2={"delete"}
          handleClose={() => setDeletePopup(null)}
          onClickBtn1={() => setDeletePopup(null)}
          onClickBtn2={() => {
            handleDelete(deletePopup);
            setDeletePopup(null);
          }}
          loading2={isCitationDeletePending}
        />
      )}
    </Fragment>
  );
};

export default Library;
